/**
 * Load the Met videoJS plugin bundles into the page exactly once each, so
 * they can register themselves on `window.videojs`. Returns a promise that
 * resolves after the script has executed (and therefore after the plugins
 * have called `videojs.registerPlugin`).
 *
 * The bundles are served from the website's own origin (built into
 * `public/` by `tools/build-plugins.js`), so this works the same way in
 * development (localhost:3000) and production (ondemand.metopera.org).
 *
 * Why not preload at app boot? videoJS is set on `window` only when the
 * Brightcove player base bundle has finished loading. The Met plugin
 * IIFEs check `window.videojs` and either call `videojs.registerPlugin`
 * (if available) or fall back to a non-player namespace; we only get a
 * working player from the first branch. So the plugins must load *after*
 * brightcovePlayerLoader resolves, not before.
 */

// jQuery is a hard dependency of the Met plugin code (the bundle uses
// `$.ajax`, `$()`, etc.). The previous flow relied on Brightcove's player
// config listing jQuery first in its `scripts:` array; the bare
// `default_default` embed has no scripts array, so we must load it
// ourselves. Same URL the previous config used.
const JQUERY_URL = "https://cdn.jsdelivr.net/jquery/2.1.4/jquery.min.js";

const PLUGIN_BUNDLES = {
  "long-form": {
    js: "/met-player-plugins.js",
    css: "/met-player-plugins.css"
  },
  broadcast: {
    js: "/met-broadcast-player-plugins.js",
    css: "/met-broadcast-player-plugins.css"
  }
};

const inflight: { [key: string]: Promise<void> } = {};
let jqueryPromise: Promise<void> | null = null;

/**
 * Whether the host page's videoJS instance has already registered the named
 * Met plugin. True for the legacy "compiled embed" path where Brightcove
 * Studio bakes the plugin code into `<playerID>_default/index.min.js` —
 * loading our bundle on top would cause double-registration warnings and
 * is a no-op anyway. False for the "bare embed" path (`default_default`)
 * where we MUST load the bundle ourselves.
 */
function pluginAlreadyRegistered(name: string): boolean {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const vjs = (window as any).videojs;
  if (!vjs || typeof vjs.getPlugins !== "function") return false;
  return Object.prototype.hasOwnProperty.call(vjs.getPlugins(), name);
}

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      resolve();
      return;
    }
    const script = document.createElement("script");
    script.src = src;
    script.async = false;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
    document.head.appendChild(script);
  });
}

function ensureJQuery(): Promise<void> {
  // If a host page already provides jQuery (legacy compiled Brightcove embed
  // path), don't double-load.
  if (typeof (window as any).$ === "function" && (window as any).jQuery) {
    return Promise.resolve();
  }
  if (jqueryPromise) return jqueryPromise;
  jqueryPromise = loadScript(JQUERY_URL);
  return jqueryPromise;
}

function loadOnce(key: keyof typeof PLUGIN_BUNDLES, sentinelPlugin: string): Promise<void> {
  const { js, css } = PLUGIN_BUNDLES[key];

  // QA #5 fix: `brightcovePlayerLoader.reset()` (called by Player.tsx's
  // disposePlayer) deletes `window.videojs` as part of its cleanup. When the
  // player re-mounts (e.g. after login from a performance detail page), the
  // new videojs is freshly recreated by the loader with NO plugins. If our
  // inflight promise is still cached from the previous mount, we'd return it
  // immediately — but the bundle's IIFEs already ran against the now-deleted
  // videojs, so MOoD isn't actually on the fresh one. Detect this case
  // (script tag in DOM but sentinel plugin missing) and force a re-load.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const hasVjs = !!(window as any).videojs;
  if (inflight[key] && hasVjs && !pluginAlreadyRegistered(sentinelPlugin)) {
    delete inflight[key];
    const existing = document.querySelector(`script[src="${js}"]`);
    if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
  }

  if (inflight[key]) return inflight[key];

  // If videoJS already has this plugin registered, the player config still
  // points at a compiled embed (e.g. EepRWtbWA_default/index.min.js) that
  // bakes the plugin code in. Skip our load to avoid double-registration.
  // Cut over by switching env REACT_APP_BRIGHTCOVE_PLAYER_ID_* to "default".
  if (pluginAlreadyRegistered(sentinelPlugin)) {
    inflight[key] = Promise.resolve();
    return inflight[key];
  }

  // CSS — fire and forget, don't block player init on it
  if (!document.querySelector(`link[href="${css}"]`)) {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = css;
    document.head.appendChild(link);
  }

  // jQuery first, then the plugin bundle. Order matters — the bundle's
  // top-level IIFEs reference `$` and `jQuery`.
  inflight[key] = ensureJQuery().then(() => loadScript(js));
  return inflight[key];
}

// Use MOoD / MetBroadcastPlayer as the sentinel plugin to detect whether a
// compiled embed has already loaded the bundle. These are the orchestrator
// plugins — every other Met* plugin co-registers with them.
export const ensureLongFormPluginsLoaded = (): Promise<void> => loadOnce("long-form", "MOoD");
export const ensureBroadcastPluginsLoaded = (): Promise<void> => loadOnce("broadcast", "MetBroadcastPlayer");
